/**
 * Minimal IndexedDB wrapper. No dependency — the app only needs one object
 * store (the offline outbox) plus a small key/value cache, and IDB Blob
 * support is what lets a queued note keep its photos across a phone reboot.
 */

const DB_NAME = 'windmill'
const DB_VERSION = 1

export const STORE_OUTBOX = 'outbox'
export const STORE_CACHE = 'cache'

let dbPromise: Promise<IDBDatabase> | null = null

export function openDb(): Promise<IDBDatabase> {
  dbPromise ??= new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_OUTBOX)) {
        const store = db.createObjectStore(STORE_OUTBOX, { keyPath: 'id' })
        store.createIndex('createdAt', 'createdAt')
      }
      if (!db.objectStoreNames.contains(STORE_CACHE)) {
        db.createObjectStore(STORE_CACHE)
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
  return dbPromise
}

function tx<T>(
  store: string, mode: IDBTransactionMode, run: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDb().then((db) => new Promise<T>((resolve, reject) => {
    const transaction = db.transaction(store, mode)
    const request = run(transaction.objectStore(store))
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  }))
}

export const idb = {
  get: <T>(store: string, key: IDBValidKey) => tx<T>(store, 'readonly', (s) => s.get(key) as IDBRequest<T>),
  getAll: <T>(store: string) => tx<T[]>(store, 'readonly', (s) => s.getAll() as IDBRequest<T[]>),
  put: (store: string, value: unknown, key?: IDBValidKey) =>
    tx(store, 'readwrite', (s) => s.put(value, key)),
  delete: (store: string, key: IDBValidKey) => tx(store, 'readwrite', (s) => s.delete(key)),
  clear: (store: string) => tx(store, 'readwrite', (s) => s.clear()),
}
